import { create } from 'zustand';
import { useRightSidebar } from './RightSidebar';

/**
 * Right-panel form host.
 *
 * Instead of raising modal dialogs, entity create/edit forms render in the
 * right sidebar — the same rail that normally shows the AI Assistant. When a
 * form is requested the assistant is hidden, the panel is force-opened (even
 * if it was collapsed), and the matching form renders. Closing the form
 * restores the panel to its previous open/collapsed state and brings the
 * assistant back.
 *
 * `kind` keys into the switch in `FormPanelHost`; add a case there (and widen
 * this union) when you migrate the next dialog. `context` carries whatever the
 * form needs (brandId, entityId, initial values…).
 */
export type FormPanelKind = 'project' | 'xotski' | 'event' | 'station-preview';

export interface FormPanelRequest {
  kind: FormPanelKind;
  mode: 'create' | 'edit';
  /** Already-localized header title for the panel. */
  title: string;
  /** Form-specific payload (brandId, entityId, initial data…). */
  context?: Record<string, unknown>;
  /** Fired after a successful save (e.g. to navigate to the new entity). */
  onDone?: (result?: unknown) => void;
}

interface FormPanelState {
  active: FormPanelRequest | null;
  /** Right-sidebar open state captured when the form opened, restored on close. */
  _prevOpen: boolean;
  openForm: (req: FormPanelRequest) => void;
  /**
   * Close the form. By default restores the rail to its pre-form state
   * (cancel). Pass `{ collapse: true }` after a successful save so the rail
   * auto-collapses once the user is redirected to the new entity.
   */
  closeForm: (opts?: { collapse?: boolean }) => void;
}

export const useFormPanel = create<FormPanelState>((set, get) => ({
  active: null,
  _prevOpen: false,
  openForm: (req) => {
    const right = useRightSidebar.getState();
    set({ active: req, _prevOpen: right.open });
    // Force the rail open so the form is visible even when collapsed.
    right.setOpen(true);
  },
  closeForm: (opts) => {
    const prev = get()._prevOpen;
    set({ active: null });
    // On a successful save we collapse the rail; on cancel we restore it.
    useRightSidebar.getState().setOpen(opts?.collapse ? false : prev);
  },
}));
